import React from "react";
import { REVIEW_COMPONENTS, previewReviewPoints } from "@shared/gamification";

// The strength meter above the submit button: a four-segment bar that fills as the review gains
// detail, with the points it would earn shown live beside it (spec §7a).
//
// Presentational and pure. The rule table comes from `@shared/gamification`, the same module the
// backend's `computeReviewBreakdown` is expressed on top of, so the preview cannot promise points
// the ledger will not pay.
//
// Three things this deliberately does NOT do:
//
//  - NO WORD LABELS on the bar. Calling a half-finished review «ضعيف» to someone about to submit
//    it is friction that costs submissions, and at 26 native reviews lifetime that trade is not
//    close.
//  - NO CHARACTER TARGET, ever. A visible floor produces padding, `generic_text` in
//    reviewAuthenticityService then penalises the padding, and the user loses twice.
//  - NO GATING. It does not touch `canSubmit`; a rating-only review stays submittable.
//
// One nudge at a time, for the highest-value unearned segment. Three nudges at once is nagging.

export type ReviewStrengthMeterProps = {
  text: string;
  imagesCount: number;
  factorsCount: number;
};

const SEGMENT_GAP = 4;

const ReviewStrengthMeter: React.FC<ReviewStrengthMeterProps> = ({
  text,
  imagesCount,
  factorsCount,
}) => {
  const preview = previewReviewPoints({
    text,
    images: imagesCount,
    factors: factorsCount,
  });

  const pointsLabel = `+${preview.total} نقطة`;

  // `REVIEW_COMPONENTS` is ordered by value, so the first unearned one is also the most valuable
  // thing the writer could add next.
  const nextNudge =
    REVIEW_COMPONENTS.find(
      (component) =>
        !!component.nudgeAr &&
        !preview.components.find((c) => c.action === component.action)?.earned
    )?.nudgeAr ?? null;

  return (
    <div className="rounded-lg border border-gray-200 p-4" dir="rtl">
      <div className="flex items-center gap-3">
        <div
          role="img"
          // The bar carries no text, so without this it is silent to a screen reader.
          aria-label={`قوة التقييم: ${preview.earnedSegments} من ${preview.totalSegments}، ${pointsLabel}`}
          style={{ display: "flex", gap: SEGMENT_GAP, flex: 1 }}
        >
          {preview.components.map((component) => (
            <span
              key={component.action}
              style={{
                flex: 1,
                height: 8,
                borderRadius: 999,
                background: component.earned ? "var(--ys-primary)" : "var(--ys-rule)",
                transition: "background 150ms ease",
              }}
            />
          ))}
        </div>
        <span
          className="font-arabic"
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--ys-primary)",
            whiteSpace: "nowrap",
          }}
        >
          {pointsLabel}
        </span>
      </div>

      {nextNudge && (
        <p className="font-arabic mt-2 text-sm" style={{ color: "var(--ys-ink-2)" }}>
          {nextNudge}
        </p>
      )}
    </div>
  );
};

export default ReviewStrengthMeter;
